import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Tooltip from '@/components/UI/Tooltip';
import { _isBlank } from '@/littledash';
import cn from 'classnames';
import type { FC } from 'react';
import type { DataTableCellIndexCoordinate } from '@/components/UI/DataTableV2/DataTable.model';
import styles from '../DataTableV2.module.scss';
import { useDataTable } from '../DataTableProvider';
import { useCellCoordinate } from '../hooks/useCellCoordinate';
import { useCellData } from '../hooks/useCellData';
import { useCellPositioning } from '../hooks/useCellPositioning';
import { useCellSelectionState } from '../hooks/useCellSelectionState';
import { useCellState } from '../hooks/useCellState';

interface SampleCollectedAtCellProps {
  coordinate: DataTableCellIndexCoordinate;
  positionOffset: { x: number; y: number };
}

/**
 * Pure display cell for sample collected-at datetime columns.
 *
 * Renders the datetime value (or "+ Collect" placeholder when empty) with no
 * editing affordances. Editing is handled by the centralized SingleCellEditor via
 * DateCellEditor.
 */
export const SampleCollectedAtCell: FC<SampleCollectedAtCellProps> = ({ coordinate, positionOffset }) => {
  const { dataTableService } = useDataTable();

  const { idCoordinate, cellRef } = useCellCoordinate(coordinate);
  const { cellData } = useCellData(idCoordinate, cellRef);
  const { cellState, tooltip } = useCellState(idCoordinate, cellRef);
  const { selectionState } = useCellSelectionState(coordinate, idCoordinate);
  const { containerStyle } = useCellPositioning(positionOffset);

  const hasGroupBorder = dataTableService.rightBorderType(coordinate.column) === 'group-separator';
  const showLoading = cellState === 'Loading';
  const isEmpty = _isBlank(cellData?.value);

  return (
    <div
      className={cn(styles.CellContainer, { [styles.groupBorder]: hasGroupBorder })}
      style={containerStyle}
      data-cell-coordinate={`${coordinate.column},${coordinate.row}`}
    >
      {showLoading ? (
        <div className={cn(styles.Cell, styles.Loading)} />
      ) : (
        <Tooltip render={tooltip}>
          <div
            className={cn(styles.Cell, styles[selectionState], styles[cellState])}
            data-test-component="SampleCollectedAtCell"
            data-test-element="collect-cell"
            data-test-key={`(${coordinate.column},${coordinate.row})`}
          >
            {isEmpty ? (
              <span className="link blue">+ Collect</span>
            ) : (
              <DateTimeRenderer value={cellData?.value ?? undefined} defaultResponse={cellData?.value ?? undefined} />
            )}
          </div>
        </Tooltip>
      )}
    </div>
  );
};
